import { capabilitiesForRoles, normalizeRoles, type Capability } from './capabilities';

/**
 * What the API says this user may do - `GET /api/v1/me`.
 *
 * The RBAC design resolves permissions on the server, per request, from group
 * mappings: the token carries identity only. This is the portal's read of that
 * answer. Screens still ask for capabilities; this file is the one place a
 * server permission (`valuation:approve`) becomes a portal capability
 * (`assignments.review`).
 *
 * As before, nothing here is a security boundary. Every endpoint repeats its
 * own check, so a capability drawn wrongly shows a button the API will refuse.
 */

export interface ServerAssignment {
  role: string;
  scopeType: string;
  scope: string;
  expiresAt: string | null;
  source: string;
  origin: string | null;
}

export interface ServerAccess {
  subjectId: string;
  email: string | null;
  displayName: string | null;
  isExternal: boolean;
  homeRegion: string | null;
  currentRegion: string | null;
  roles: string[];
  permissions: string[];
  assignments: ServerAssignment[];
}

/**
 * Server permissions to capabilities. A capability is granted when the user
 * holds any one of the listed permissions.
 *
 * `null` means the server has no permission for it yet and the local role
 * table still decides - only Ratings, a separate product gated by its own group.
 */
const CAPABILITY_PERMISSIONS: Record<Capability, readonly string[] | null> = {
  'assignments.view': ['valuation:read'],
  'assignments.create': ['valuation:create'],
  'assignments.edit': ['valuation:update'],
  'assignments.export': ['report:export'],
  'assignments.delete': ['valuation:delete'],
  'assignments.restore': ['role:manage'],
  'assignments.complete': ['valuation:approve'],
  'assignments.submitForReview': ['valuation:update'],
  'assignments.review': ['valuation:approve'],
  'assignments.reject': ['valuation:update'],
  'assignments.hold': ['valuation:update'],
  'assignments.reactivate': ['role:manage'],
  'assignments.remarks.internal.read': ['valuation:update'],
  'assignments.remarks.internal.write': ['valuation:approve'],
  'assignments.remarks.client.write': ['valuation:update', 'comment:create'],
  'assignments.masterData': ['mapping:manage'],
  'assignments.assist': ['valuation:read'],
  'assignments.bulk': ['valuation:approve'],

  'crm.view': ['client:read', 'valuation:read'],
  'crm.edit': ['contact:update', 'client:update'],
  'opportunities.view': ['engagement:read'],
  'opportunities.edit': ['engagement:create', 'engagement:update'],
  'opportunities.approve': ['deal:approve'],

  'reportStudio.view': ['valuation:update'],
  'reportStudio.authorTemplates': ['document:publish'],
  'agents.view': ['valuation:read'],
  'agents.build': ['valuation:update'],
  'map.view': ['valuation:read', 'market:read'],
  'vault.view': ['document:read'],
  'connectors.view': ['valuation:update'],
  'ratings.view': null,

  'platform.settings': ['role:manage']
};

/** Capabilities from the server's permissions, with the local table for the unmapped few. */
export function capabilitiesFromServer(access: ServerAccess, localRoles: readonly string[] | undefined): Set<Capability> {
  const held = new Set(access.permissions);
  const local = capabilitiesForRoles(normalizeRoles(localRoles));
  const granted = new Set<Capability>();

  for (const [capability, permissions] of Object.entries(CAPABILITY_PERMISSIONS) as [Capability, readonly string[] | null][]) {
    const allowed = permissions === null ? local.has(capability) : permissions.some((permission) => held.has(permission));

    if (allowed) {
      granted.add(capability);
    }
  }

  return granted;
}
